import React, { useState } from 'react';
import Header from '../components/Header.tsx';
import HotelCard from '../components/HotelCard.tsx';
import BookingModal from '../components/BookingModal.tsx';
import Footer from '../components/Footer.tsx';
import Modal from '../components/Modal.tsx';
import Map from '../components/Map.tsx';
import hotel from '../images/hotel.jpg'
import { MapPin, Star, Users, Wifi, Coffee, Dumbbell } from 'lucide-react';

const hotels = [
  {
    id: 1,
    name: "Grand Casino Resort & Spa",
    description: "Experience luxury at its finest with our world-class casino, spa, and entertainment venues. Featuring 500+ rooms, 24/7 gaming, and Michelin-starred dining.",
    image: "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&q=80&w=1200",
    rating: 4.8,
    amenities: ["5-Star Dining", "Luxury Spa", "VIP Gaming Rooms", "Rooftop Pool"],
    price: "$299"
  },
  {
    id: 2,
    name: "Royal Fortune Hotel & Casino",
    description: "Where elegance meets excitement. Featuring premium gaming tables, 5-star accommodations, and breathtaking city views from our signature rooftop restaurant.",
    image: "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&q=80&w=1200",
    rating: 4.9,
    amenities: ["Rooftop Restaurant", "Premium Tables", "Luxury Suites", "24/7 Service"],
    price: "$399"
  },
  {
    id: 3,
    name: "Maple Leaf Casino Resort",
    description: "A true Canadian gaming experience with stunning views and exceptional service. Our newly renovated property features modern amenities and classic charm.",
    image: "https://images.unsplash.com/photo-1578991624414-276ef23a534f?auto=format&fit=crop&q=80&w=1200",
    rating: 4.7,
    amenities: ["Mountain Views", "Canadian Cuisine", "Poker Room", "Wellness Center"],
    price: "$259"
  },
  {
    id: 4,
    name: "Diamond Sky Casino",
    description: "Toronto's premier destination for luxury gaming and entertainment. Experience unparalleled service in our award-winning casino resort.",
    image: "https://images.unsplash.com/photo-1606402179428-a57976d71fa4?auto=format&fit=crop&q=80&w=1200",
    rating: 4.9,
    amenities: ["Sky Lounge", "Diamond Club", "Private Gaming", "Luxury Spa"],
    price: "$459"
  },
  {
    id: 5,
    name: "Nova Scotia Harbor Casino",
    description: "Overlooking the Atlantic Ocean, our resort combines maritime charm with world-class gaming. Featuring fresh seafood and stunning ocean views.",
    image: "https://images.unsplash.com/photo-1594741158704-5a784b8e59fb?auto=format&fit=crop&q=80&w=1200",
    rating: 4.6,
    amenities: ["Ocean Views", "Seafood Restaurant", "Marina Access", "Beach Club"],
    price: "$279"
  },
  {
    id: 6,
    name: "Montreal Royale Casino",
    description: "In the heart of Montreal, experience European elegance and modern luxury. Our French-inspired resort offers the finest in entertainment and dining.",
    image: {hotel},
    rating: 4.8,
    amenities: ["French Cuisine", "Wine Cellar", "Art Gallery", "Theater"],
    price: "$379"
  }
];

const amenities = [
  { icon: <Star className="w-6 h-6" />, title: "5-Star Service", description: "24/7 concierge and personalized attention" },
  { icon: <Users className="w-6 h-6" />, title: "VIP Treatment", description: "Exclusive access to premium facilities" },
  { icon: <Wifi className="w-6 h-6" />, title: "High-Speed WiFi", description: "Complimentary throughout the property" },
  { icon: <Coffee className="w-6 h-6" />, title: "Fine Dining", description: "Multiple award-winning restaurants" },
  { icon: <Dumbbell className="w-6 h-6" />, title: "Fitness Center", description: "State-of-the-art equipment & classes" },
  { icon: <MapPin className="w-6 h-6" />, title: "Prime Location", description: "Central to major attractions" }
];

function App() {
  const [bookingModal, setBookingModal] = useState({ isOpen: false, hotelName: '' });
  const [currentModal, setCurrentModal] = useState<{ type: 'privacy' | 'terms' | 'contact' | null, isOpen: boolean }>({
    type: null,
    isOpen: false
  });

  const handleOpenModal = (type: 'privacy' | 'terms' | 'contact') => {
    setCurrentModal({ type, isOpen: true });
  };

  const handleCloseModal = () => {
    setCurrentModal({ type: null, isOpen: false });
  };

  const getModalContent = () => {
    switch (currentModal.type) {
      case 'privacy':
        return (
          <div className="prose max-w-none">
            <h2>Privacy Policy</h2>
            <p>Your privacy is important to us. This privacy policy explains how we collect, use, and protect your personal information.</p>
          </div>
        );
      case 'terms':
        return (
          <div className="prose max-w-none">
            <h2>Terms & Conditions</h2>
            <p>By using our services, you agree to these terms and conditions.</p>
          </div>
        );
      case 'contact':
        return (
          <form className="space-y-4">
            <div>
              <label className="block text-gray-700 mb-2">Name</label>
              <input type="text" className="w-full p-3 border rounded-lg" />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Email</label>
              <input type="email" className="w-full p-3 border rounded-lg" />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Message</label>
              <textarea className="w-full p-3 border rounded-lg h-32"></textarea>
            </div>
            <button type="submit" className="w-full bg-blue-700 text-white py-3 rounded-lg hover:bg-blue-800">
              Send Message
            </button>
          </form>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-blue-100 to-blue-200">
      <Header onOpenModal={handleOpenModal} />
      
      <main className="relative">
        {/* Featured Hotels Section */}
        <section className="container mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Luxury Casino Resorts</h2>
            <p className="text-xl text-gray-600">Discover Finland's Finest Gaming Destinations</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {hotels.map((hotel) => (
              <HotelCard
                key={hotel.id}
                {...hotel}
                onBookNow={() => setBookingModal({ isOpen: true, hotelName: hotel.name })}
              />
            ))}
          </div>
        </section>

        {/* Amenities Section */}
        <section className="py-16 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 text-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">World-Class Amenities</h2>
              <p className="text-xl text-blue-200">Experience Unparalleled Luxury</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {amenities.map((amenity, index) => (
                <div key={index} className="bg-blue-800/50 backdrop-blur-sm p-6 rounded-lg hover:bg-blue-700/50 transition-colors">
                  <div className="flex items-center mb-4">
                    {amenity.icon}
                    <h3 className="text-xl font-semibold ml-3">{amenity.title}</h3>
                  </div>
                  <p className="text-blue-200">{amenity.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-16 bg-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-900/10 to-transparent pointer-events-none"></div>
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Find Our Locations</h2>
              <p className="text-xl text-gray-600">Discover Luxury Across Finland</p>
            </div>
            <div className="relative">
              <Map className="rounded-xl shadow-2xl" />
              <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm p-4 rounded-lg shadow-lg">
                <h3 className="font-bold text-lg mb-2">BlueLakeEscapesFI Vancouver</h3>
                <p className="text-gray-600">123 Casino Drive, Vancouver, BC</p>
                <p className="text-gray-600">Open 24/7</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <BookingModal
        isOpen={bookingModal.isOpen}
        onClose={() => setBookingModal({ isOpen: false, hotelName: '' })}
        hotelName={bookingModal.hotelName}
      />

      <Modal
        isOpen={currentModal.isOpen}
        onClose={handleCloseModal}
        title={currentModal.type ? currentModal.type.charAt(0).toUpperCase() + currentModal.type.slice(1) : ''}
      >
        {getModalContent()}
      </Modal>
    </div>
  );
}

export default App;